import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown } from "lucide-react";
import { api, gbp } from "../lib/api";
import { auctionPrice, nextDropIn, auctionStartsIn, fmtSecs } from "../lib/auction";
import { MaskedLines, FadeUp } from "../components/Reveal";

const AuctionCard = ({ a }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const live = a.state === "live";
  const scheduled = a.state === "scheduled";
  const price = live ? auctionPrice(a) : scheduled ? a.start_price : a.claimed_price;
  const pct = ((a.start_price - price) / (a.start_price - a.floor_price)) * 100;

  return (
    <Link to={`/reverse-auction/${a.slug}`} data-testid={`auction-card-${a.slug}`} className={`block border bg-[#111111] group ${live ? "border-[#FF3B30] hover:border-[#FFCC00]" : scheduled ? "border-[#FFCC00]" : "border-[#27272A] opacity-70"} transition-colors duration-200`}>
      <div className="relative overflow-hidden aspect-[16/9]">
        <img src={a.images[0]} alt={a.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-500" />
        <span className={`absolute top-3 left-3 text-[11px] uppercase tracking-widest px-3 py-1 font-bold ${live ? "bg-[#FF3B30] text-white" : scheduled ? "bg-[#FFCC00] text-black" : "bg-[#27272A] text-white/60"}`}>
          {live ? "● PRICE FALLING" : scheduled ? `STARTS IN ${fmtSecs(auctionStartsIn(a))}` : a.state === "ended" ? "ENDED" : `CLAIMED BY ${a.claimed_by_name?.split(" ")[0] || "MEMBER"}`}
        </span>
      </div>
      <div className="p-6">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{a.sku} · {a.condition} · {a.units} units</div>
        <h3 className="font-display text-2xl leading-tight mb-4">{a.title}</h3>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase text-white/40">{scheduled ? "Starting price" : "Current price"}</div>
            <div className={`font-display text-4xl ${live || scheduled ? "text-[#FFCC00]" : "text-white/50"}`}>{price != null ? gbp(price) : "—"}</div>
          </div>
          {live && (
            <div className="text-right text-xs">
              <div className="text-white/40 uppercase text-[10px]">Next drop</div>
              <div className="font-display text-2xl text-[#FF3B30]">{nextDropIn(a)}S</div>
              <div className="text-white/30">−{gbp(a.drop_amount)} / {a.interval_seconds}s</div>
            </div>
          )}
        </div>
        <div className="mt-4 h-1.5 bg-[#27272A]">
          <div className="h-full bg-[#FF3B30] transition-[width] duration-1000" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
        <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/30 mt-2">
          <span>Started {gbp(a.start_price)}</span><span>Floor {gbp(a.floor_price)}</span>
        </div>
      </div>
    </Link>
  );
};

export default function AuctionsPage() {
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    const load = () => api.get("/auctions").then(({ data }) => setAuctions(data));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
      <div className="flex items-center gap-3 text-[#FF3B30] text-xs uppercase tracking-[0.3em] mb-6">
        <TrendingDown size={14} /> Reverse Auction — Dutch Format
      </div>
      <MaskedLines lines={["THE PRICE", "FALLS. YOU", "CLAIM. IT'S", "YOURS."]} className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]" />
      <p className="text-white/50 text-sm sm:text-base mt-8 max-w-lg leading-relaxed">
        Price drops on a fixed schedule until one member hits CLAIM. First claim wins the entire lot at that exact price. Wait too long and someone else takes it. That's the game.
      </p>
      <div className="grid md:grid-cols-2 gap-6 mt-16">
        {auctions.map((a, i) => (
          <FadeUp key={a.id} delay={i * 0.1}><AuctionCard a={a} /></FadeUp>
        ))}
        {auctions.length === 0 && (
          <div className="md:col-span-2 border border-[#27272A] p-16 text-center font-display text-2xl text-white/30">NO AUCTIONS RUNNING. CHECK BACK SOON.</div>
        )}
      </div>
    </div>
  );
}
